import { useEffect, useRef } from 'react';

import { ArrowLeft } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';

import { getEnv } from '@/lib/env';

export const WidgetDemoPage = () => {
  const { configId } = useParams<{ configId: string }>();
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!configId || scriptLoaded.current) return;
    scriptLoaded.current = true;

    const apiUrl = getEnv('VITE_API_URL');
    const script = document.createElement('script');
    script.src = `${apiUrl}/widget/${configId}/loader`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
      const widget = document.querySelector('.carpass-widget');
      if (widget) widget.remove();
    };
  }, [configId]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a0f1c 0%, #111827 40%, #0f172a 100%)',
        color: 'white',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow effects */}
      <div
        style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(37,99,235,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Grid texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      <header
        style={{
          position: 'relative',
          padding: '24px 40px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Link
          to="/configurations"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 16px',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
          }}
        >
          <ArrowLeft size={16} />
          Back to Configurations
        </Link>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#22c55e',
              display: 'inline-block',
              boxShadow: '0 0 8px rgba(34,197,94,0.4)',
            }}
          />
          <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Widget Active
          </span>
        </div>
      </header>

      {/* Center content */}
      <main
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 73px)',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.3)',
            marginBottom: '16px',
          }}
        >
          Widget Preview
        </div>

        <h1
          style={{
            fontSize: 'clamp(28px, 4vw, 48px)',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            lineHeight: 1.1,
            margin: '0 0 16px',
            background: 'linear-gradient(135deg, white, rgba(255,255,255,0.6))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Test Your Voice Agent
        </h1>

        <p
          style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.4)',
            maxWidth: '480px',
            lineHeight: 1.6,
            margin: '0 0 40px',
          }}
        >
          Click the call button in the bottom-right corner to start a live conversation with your AI
          assistant. Speak naturally — results will appear on screen.
        </p>

        {/* Decorative orb */}
        <div
          style={{
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 40% 40%, rgba(37,99,235,0.15), rgba(37,99,235,0.03) 60%, transparent 80%)',
            border: '1px solid rgba(37,99,235,0.1)',
            animation: 'float 6s ease-in-out infinite',
          }}
        />

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        `}</style>
      </main>
    </div>
  );
};
